import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Input,
  Badge,
  Card,
  CardBody,
  Spinner,
  FormGroup,
  Label,
} from "reactstrap";
import toast from "react-hot-toast";

const AddDeviceModal = ({
  deviceModalOpen,
  setDeviceModalOpen,
  devicesLoadingApi,
  availableDevices = [],
  selectedNewDevices = [],
  toggleNewDeviceSelection,
  handleAssignNewDevices,
  assigningDevices,
  getDeviceImageUrl,
  fetchDevicesBySearch,
  setAvailableDevices,
  isSearching = false,
  assignedDevices = [],
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Filter devices: only show devices with macId, isRegistrationTokenUsed === true, and not already assigned
  const filterValidDevices = (devices) => {
    return devices.filter(
      (device) => 
        device.macId && 
        device.macId.trim() !== "" && 
        device.isRegistrationTokenUsed === true &&
        !assignedDevices.some((assigned) => assigned.macId === device.macId)
    );
  };

  // Filter to show only organizations with valid devices
  const orgsWithDevices = availableDevices
    .map((org) => ({
      ...org,
      devices: filterValidDevices(org.devices || []),
    }))
    .filter((org) => org.devices.length > 0);

  // Fetch devices whenever searchText changes with debouncing
  useEffect(() => {
    if (deviceModalOpen) {
      fetchDevicesBySearch(searchText);
    }
  }, [searchText, deviceModalOpen, fetchDevicesBySearch]);

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
  };

  const devicesToShow = selectedOrg ? selectedOrg.devices : [];

  // Helper function to check if all devices from all orgs are selected
  const areAllDevicesSelectedGlobally = () => {
    if (orgsWithDevices.length === 0) return false;
    return orgsWithDevices.every((org) => areAllOrgDevicesSelected(org));
  };

  // Helper function to check if all devices from an org are selected
  const areAllOrgDevicesSelected = (org) => {
    return (
      org.devices &&
      org.devices.length > 0 &&
      org.devices.every((device) =>
        selectedNewDevices.some((d) => d.deviceId === device.deviceId),
      )
    );
  };

  // Helper function to check if some devices from an org are selected
  const areSomeOrgDevicesSelected = (org) => {
    return (
      org.devices &&
      org.devices.some((device) =>
        selectedNewDevices.some((d) => d.deviceId === device.deviceId),
      )
    );
  };

  // Select all devices from all organizations
  const handleSelectAllOrgs = () => {
    const allDevices = [];
    let totalAdded = 0;
    orgsWithDevices.forEach((org) => {
      org.devices.forEach((device) => {
        if (!selectedNewDevices.some((d) => d.deviceId === device.deviceId)) {
          allDevices.push(device);
          totalAdded++;
        }
      });
    });
    // Add all devices to selectedNewDevices using toggleNewDeviceSelection
    allDevices.forEach((device) => toggleNewDeviceSelection(device));
    if (totalAdded > 0) {
      toast.success(`Selected ${totalAdded} devices from all organizations`);
    } else {
      toast.success("All devices are already selected");
    }
  };

  // Deselect all devices from all organizations
  const handleDeselectAllOrgs = () => {
    let totalRemoved = 0;
    selectedNewDevices.forEach((selectedDevice) => {
      const isInOrgs = orgsWithDevices.some((org) =>
        org.devices.some(
          (device) => device.deviceId === selectedDevice.deviceId,
        ),
      );
      if (isInOrgs) {
        toggleNewDeviceSelection(selectedDevice);
        totalRemoved++;
      }
    });
    toast.success(`Deselected all devices`);
  };

  // Select all devices from currently selected organization
  const handleSelectAllCurrentOrg = () => {
    let totalAdded = 0;
    devicesToShow.forEach((device) => {
      if (!selectedNewDevices.some((d) => d.deviceId === device.deviceId)) {
        toggleNewDeviceSelection(device);
        totalAdded++;
      }
    });
    if (totalAdded > 0) {
      toast.success(`Selected ${totalAdded} devices from ${selectedOrg.name}`);
    } else {
      toast.success("All devices from this organization are already selected");
    }
  };

  // Deselect all devices from currently selected organization
  const handleDeselectAllCurrentOrg = () => {
    let totalRemoved = 0;
    devicesToShow.forEach((device) => {
      if (selectedNewDevices.some((d) => d.deviceId === device.deviceId)) {
        toggleNewDeviceSelection(device);
        totalRemoved++;
      }
    });
    if (totalRemoved > 0) {
      toast.success(
        `Deselected ${totalRemoved} devices from ${selectedOrg.name}`,
      );
    }
  };

  return (
    <Modal
      isOpen={deviceModalOpen}
      toggle={() => setDeviceModalOpen(false)}
      centered
      style={{ maxWidth: "80vw" }}
    >
      <ModalHeader toggle={() => setDeviceModalOpen(false)}>
        Add Devices to Campaign
      </ModalHeader>

      <ModalBody
        style={{
          height: "70vh",
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "1rem",
        }}
      >
        {devicesLoadingApi && availableDevices.length === 0 ? (
          <div className="text-center py-5">
            <Spinner />
            <p className="text-muted mt-2">Loading devices...</p>
          </div>
        ) : (
          <Row style={{ height: "100%", width: "100%", margin: 0 }}>
            {/* Left Side - Organizations List with single Search */}
            <Col
              md="4"
              className="border-end pe-3"
              style={{ height: "100%", overflowY: "auto" }}
            >
              {/* <h6 className="mb-3">Organizations</h6> */}
              <FormGroup>
                <Label>Search Organizations or Devices</Label>
                <div className="d-flex gap-2">
                  <div className="position-relative flex-grow-1">
                    <Input
                      type="text"
                      value={searchText}
                      placeholder="Type to search..."
                      onChange={(e) => setSearchText(e.target.value)}
                      className="pr-5" // padding-right for spinner
                    />
                    {isSearching && (
                      <Spinner
                        size="sm"
                        color="primary"
                        className="position-absolute"
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    )}
                  </div>

                  {orgsWithDevices.length > 0 && (
                    <div className="d-flex gap-2">
                      <Button
                        color={
                          areAllDevicesSelectedGlobally() ? "success" : "info"
                        }
                        outline
                        size="sm"
                        onClick={handleSelectAllOrgs}
                        disabled={areAllDevicesSelectedGlobally()}
                      >
                        {areAllDevicesSelectedGlobally()
                          ? "✓ All Selected"
                          : "Select All"}
                      </Button>
                      {areAllDevicesSelectedGlobally() && (
                        <Button
                          color="danger"
                          outline
                          size="sm"
                          onClick={handleDeselectAllOrgs}
                        >
                          Deselect All
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </FormGroup>

              <div
                style={{
                  maxHeight: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {orgsWithDevices.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      {availableDevices.length > 0
                        ? "No organizations with devices found"
                        : "No organizations found"}
                    </p>
                  </div>
                ) : (
                  orgsWithDevices.map((org) => {
                    const allSelected = areAllOrgDevicesSelected(org);
                    const someSelected = areSomeOrgDevicesSelected(org);
                    return (
                      <div
                        key={org.organizationId}
                        onClick={() => handleOrgSelect(org)}
                        className={`d-flex justify-content-between align-items-start p-2 ${
                          selectedOrg?.organizationId === org.organizationId
                            ? "" // keep default, no extra color or border
                            : allSelected
                            ? "" // no green
                            : someSelected
                            ? "" // no warning color
                            : "" // default
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        <div>
                          <div className="fw-bold">{org.name}</div>
                          <small className="text-muted">{org.orgCode}</small>
                          {allSelected && (
                            <div>
                              <small className="text-success fw-bold">
                                ✓ All selected
                              </small>
                            </div>
                          )}
                          {someSelected && !allSelected && (
                            <div>
                              <small className="text-warning fw-bold">
                                ⚬ Partially selected
                              </small>
                            </div>
                          )}
                        </div>
                        {org.devices.length > 0 && (
                          <Badge color="primary" pill>
                            {org.devices.length}
                          </Badge>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </Col>

            {/* Right Side - Devices Grid */}
            <Col
              md="8"
              style={{
                height: "100%",
                maxHeight: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {!selectedOrg ? (
                <div className="text-center py-5 text-muted">
                  <i
                    className="ri-building-line"
                    style={{ fontSize: "40px", opacity: 0.3 }}
                  />
                  <p className="mt-2">Select an organization to view devices</p>
                </div>
              ) : devicesToShow.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i
                    className="ri-inbox-2-line"
                    style={{ fontSize: "40px", opacity: 0.3 }}
                  />
                  <p className="mt-2">
                    No devices available for this organization
                  </p>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      Devices - {selectedOrg.name}
                      <Badge color="secondary" className="ms-2">
                        {devicesToShow.length}
                      </Badge>
                    </h6>
                    {devicesToShow.length > 0 && (
                      <div className="d-flex gap-2">
                        <Button
                          color={
                            areAllOrgDevicesSelected(selectedOrg)
                              ? "success"
                              : "info"
                          }
                          outline
                          size="sm"
                          onClick={handleSelectAllCurrentOrg}
                          disabled={areAllOrgDevicesSelected(selectedOrg)}
                        >
                          {areAllOrgDevicesSelected(selectedOrg)
                            ? "✓ All Selected"
                            : "Select All"}
                        </Button>
                        {areAllOrgDevicesSelected(selectedOrg) && (
                          <Button
                            color="danger"
                            outline
                            size="sm"
                            onClick={handleDeselectAllCurrentOrg}
                          >
                            Deselect All
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <Row className="g-3">
                    {devicesToShow.map((device) => {
                      const isSelected = selectedNewDevices.some(
                        (d) => d.deviceId === device.deviceId,
                      );
                      const isAlreadyAssigned = assignedDevices.some(
                        (d) => d.macId === device.macId,
                      );
                      return (
                        <Col key={device.deviceId} xs="12" sm="6" md="4" lg="4">
                          <Card
                            onClick={() =>
                              !isAlreadyAssigned &&
                              toggleNewDeviceSelection(device)
                            }
                            className={`device-card border ${
                              isAlreadyAssigned
                                ? "border-secondary"
                                : isSelected
                                ? "border-primary shadow"
                                : "border-light"
                            }`}
                            style={{
                              cursor: isAlreadyAssigned
                                ? "not-allowed"
                                : "pointer",
                              transition: "all 0.3s ease",
                              backgroundColor: isAlreadyAssigned
                                ? "rgba(108,117,125,0.05)"
                                : isSelected
                                ? "rgba(0,123,255,0.05)"
                                : "#fff",
                              opacity: isAlreadyAssigned ? 0.6 : 1,
                            }}
                            title={isAlreadyAssigned ? "Already assigned" : ""}
                          >
                            <div
                              style={{
                                height: 120,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: isAlreadyAssigned
                                  ? "#e9ecef"
                                  : "#f8f9fa",
                                borderBottom: "1px solid #e9ecef",
                                position: "relative",
                              }}
                            >
                              <img
                                src={getDeviceImageUrl()}
                                alt={device.deviceName || "Device"}
                                style={{
                                  height: "100%",
                                  maxWidth: "90%",
                                  objectFit: "contain",
                                }}
                              />
                              {isSelected && (
                                <Badge
                                  color="success"
                                  pill
                                  className="position-absolute top-0 end-0 m-2"
                                >
                                  ✓
                                </Badge>
                              )}
                              {isAlreadyAssigned && (
                                <Badge
                                  color="secondary"
                                  pill
                                  className="position-absolute top-0 end-0 m-2"
                                >
                                  Assigned
                                </Badge>
                              )}
                            </div>
                            <CardBody className="p-3">
                              <h6
                                className={`mb-2 text-truncate ${
                                  isAlreadyAssigned ? "text-muted" : ""
                                }`}
                                style={{ fontSize: "14px", fontWeight: 600 }}
                              >
                                {device.deviceName || "Device"}
                              </h6>
                              {device.location && (
                                <div
                                  className="mb-1"
                                  style={{ fontSize: "12px" }}
                                >
                                  <i className="ri-map-pin-line me-1" />
                                  <small
                                    className={
                                      isAlreadyAssigned
                                        ? "text-muted"
                                        : "text-muted"
                                    }
                                  >
                                    {device.location}
                                  </small>
                                </div>
                              )}
                              <div
                                className={`mb-2 ${
                                  isAlreadyAssigned ? "text-muted" : "text-muted"
                                }`}
                                style={{ fontSize: "12px" }}
                              >
                                <strong>MAC ID:</strong>
                                <div
                                  className="text-break"
                                  style={{
                                    fontSize: "11px",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {device.macId}
                                </div>
                              </div>
                              {/* {device.registrationToken && (
                                <div>
                                  <Badge
                                    color={
                                      isAlreadyAssigned
                                        ? "secondary"
                                        : device.isRegistrationTokenUsed
                                        ? "secondary"
                                        : "success"
                                    }
                                    className="text-uppercase"
                                    style={{ fontSize: "10px" }}
                                  >
                                    {device.registrationToken}
                                  </Badge>
                                </div>
                              )} */}
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}
            </Col>
          </Row>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={() => setDeviceModalOpen(false)}>
          Cancel
        </Button>
        <Button
          color="success"
          onClick={handleAssignNewDevices}
          disabled={selectedNewDevices.length === 0 || assigningDevices}
        >
          {assigningDevices
            ? "Assigning..."
            : `Assign (${selectedNewDevices.length})`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddDeviceModal;
